import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import * as Tabs from '@radix-ui/react-tabs';
import { motion } from 'framer-motion';
import { Fragment, useState } from 'react';
import { Download, ChevronDown, ChevronRight } from 'lucide-react';
import { MistakeTagger } from '@/components/MistakeTagger';
import type { Dividend, HistoricalOrder, Transaction } from '@t212/shared';
import { api } from '@/lib/api';
import { fmtDate, fmtMoney, fmtNumber, fmtSignedMoney } from '@/lib/format';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/history')({
  component: HistoryPage,
});

function csvFromRows(headers: string[], rows: (string | number)[][]): string {
  return [headers, ...rows]
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
}

function downloadCsv(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function HistoryPage() {
  const orders = useQuery({ queryKey: ['history-orders'], queryFn: () => api.orderHistory() });
  const divs = useQuery({ queryKey: ['history-divs'], queryFn: () => api.dividendHistory() });
  const tx = useQuery({ queryKey: ['history-tx'], queryFn: () => api.transactionHistory() });

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-4"
    >
      <div>
        <h1 className="text-xl font-semibold text-zinc-100 tracking-tight">History</h1>
        <p className="text-[12px] text-zinc-400 mt-0.5">
          Orders, dividends, cash transactions. Export any tab to CSV.
        </p>
      </div>

      <Tabs.Root defaultValue="orders" className="space-y-4">
        <Tabs.List className="inline-flex gap-2">
          {[
            { v: 'orders', label: 'Orders' },
            { v: 'divs', label: 'Dividends' },
            { v: 'tx', label: 'Transactions' },
          ].map((t) => (
            <Tabs.Trigger
              key={t.v}
              value={t.v}
              className="px-3 py-1.5 text-sm rounded-md border border-zinc-800 text-zinc-400 hover:text-zinc-200 data-[state=active]:border-indigo-500/60 data-[state=active]:bg-indigo-500/15 data-[state=active]:text-indigo-100"
            >
              {t.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <Tabs.Content value="orders">
          <OrdersTable
            items={orders.data?.items ?? []}
            onExport={() =>
              downloadCsv(
                'orders.csv',
                csvFromRows(
                  ['Date', 'Ticker', 'Side', 'Quantity', 'Fill price', 'Status', 'Result'],
                  (orders.data?.items ?? []).map((o) => [
                    o.createdAt,
                    o.ticker,
                    o.side,
                    o.quantity,
                    o.fillPrice ?? '',
                    o.status,
                    o.result ?? '',
                  ]),
                ),
              )
            }
          />
        </Tabs.Content>
        <Tabs.Content value="divs">
          <DividendsTable
            items={divs.data?.items ?? []}
            onExport={() =>
              downloadCsv(
                'dividends.csv',
                csvFromRows(
                  ['Date', 'Ticker', 'Amount', 'Currency', 'Type'],
                  (divs.data?.items ?? []).map((d) => [
                    d.paidAt,
                    d.ticker,
                    d.amount,
                    d.currency,
                    d.type ?? '',
                  ]),
                ),
              )
            }
          />
        </Tabs.Content>
        <Tabs.Content value="tx">
          <TransactionsTable
            items={tx.data?.items ?? []}
            onExport={() =>
              downloadCsv(
                'transactions.csv',
                csvFromRows(
                  ['Date', 'Type', 'Amount', 'Currency', 'Reference'],
                  (tx.data?.items ?? []).map((t) => [
                    t.createdAt,
                    t.type,
                    t.amount,
                    t.currency,
                    t.reference ?? '',
                  ]),
                ),
              )
            }
          />
        </Tabs.Content>
      </Tabs.Root>
    </motion.div>
  );
}

function TableShell({
  onExport,
  children,
}: {
  onExport: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 overflow-hidden">
      <div className="flex items-center justify-end px-3 py-2 border-b border-zinc-800">
        <button
          type="button"
          onClick={onExport}
          className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border border-zinc-800 text-zinc-300 hover:bg-zinc-900"
        >
          <Download className="size-3.5" /> CSV
        </button>
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

function OrdersTable({
  items,
  onExport,
}: {
  items: HistoricalOrder[];
  onExport: () => void;
}) {
  const [expanded, setExpanded] = useState<number | null>(null);
  return (
    <TableShell onExport={onExport}>
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase text-zinc-400">
          <tr>
            <Th className="w-6">{''}</Th>
            <Th>Date</Th>
            <Th>Ticker</Th>
            <Th>Side</Th>
            <Th>Quantity</Th>
            <Th>Fill</Th>
            <Th>Status</Th>
            <Th className="text-right">Result</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((o) => (
            <Fragment key={o.id}>
            <tr
              className="border-t border-zinc-900 hover:bg-zinc-900/40 cursor-pointer"
              onClick={() => setExpanded(expanded === o.id ? null : o.id)}
            >
              <Td>
                {expanded === o.id ? (
                  <ChevronDown className="size-3 text-zinc-400" />
                ) : (
                  <ChevronRight className="size-3 text-zinc-400" />
                )}
              </Td>
              <Td>{fmtDate(o.createdAt)}</Td>
              <Td className="font-mono text-zinc-300">{o.ticker.split('_')[0]}</Td>
              <Td>
                <span
                  className={cn(
                    'text-[11px] px-1.5 py-0.5 rounded border',
                    o.side === 'BUY'
                      ? 'border-emerald-500/40 text-emerald-300'
                      : 'border-rose-500/40 text-rose-300',
                  )}
                >
                  {o.side}
                </span>
              </Td>
              <Td className="num">{fmtNumber(o.quantity, 0)}</Td>
              <Td className="num">{o.fillPrice ? fmtNumber(o.fillPrice) : '—'}</Td>
              <Td className="text-xs text-zinc-400">{o.status}</Td>
              <Td className="text-right num">
                {o.result !== undefined ? (
                  <span className={o.result >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                    {fmtSignedMoney(o.result)}
                  </span>
                ) : (
                  '—'
                )}
              </Td>
            </tr>
            {expanded === o.id && o.side === 'SELL' && (
              <tr className="border-t border-zinc-900 bg-zinc-950/60">
                <td colSpan={8} className="p-0">
                  {/* Pin the tagger to the viewport edge so horizontal scrolling
                      of the wide table doesn't push the post-mortem flow off-screen. */}
                  <div className="sticky left-0 px-3 py-3 max-w-screen-md">
                    <MistakeTagger orderId={o.id} />
                  </div>
                </td>
              </tr>
            )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </TableShell>
  );
}

function DividendsTable({ items, onExport }: { items: Dividend[]; onExport: () => void }) {
  return (
    <TableShell onExport={onExport}>
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase text-zinc-400">
          <tr>
            <Th>Date</Th>
            <Th>Ticker</Th>
            <Th>Type</Th>
            <Th className="text-right">Amount</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((d) => (
            <tr key={d.id} className="border-t border-zinc-900 hover:bg-zinc-900/40">
              <Td>{fmtDate(d.paidAt)}</Td>
              <Td className="font-mono text-zinc-300">{d.ticker.split('_')[0]}</Td>
              <Td className="text-xs text-zinc-400">{d.type ?? '—'}</Td>
              <Td className="text-right num text-emerald-300">{fmtMoney(d.amount, d.currency)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableShell>
  );
}

function TransactionsTable({
  items,
  onExport,
}: {
  items: Transaction[];
  onExport: () => void;
}) {
  return (
    <TableShell onExport={onExport}>
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase text-zinc-400">
          <tr>
            <Th>Date</Th>
            <Th>Type</Th>
            <Th>Reference</Th>
            <Th className="text-right">Amount</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((t) => (
            <tr key={t.id} className="border-t border-zinc-900 hover:bg-zinc-900/40">
              <Td>{fmtDate(t.createdAt)}</Td>
              <Td>{t.type}</Td>
              <Td className="text-xs text-zinc-400">{t.reference ?? '—'}</Td>
              <Td
                className={cn(
                  'text-right num',
                  t.amount >= 0 ? 'text-emerald-300' : 'text-zinc-300',
                )}
              >
                {fmtSignedMoney(t.amount, t.currency)}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableShell>
  );
}

function Th({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={cn(
        'text-left font-normal px-3 py-2 whitespace-nowrap border-b border-zinc-800',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={cn('px-3 py-2.5 whitespace-nowrap', className)}>{children}</td>;
}
